import {
  Component,
  ErrorInfo,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface BoundaryProps {
  children: ReactNode;
  fallback: ReactNode;
}

interface BoundaryState {
  hasError: boolean;
}

class MarkdownErrorBoundary extends Component<BoundaryProps, BoundaryState> {
  public state: BoundaryState = { hasError: false };

  public static getDerivedStateFromError(): BoundaryState {
    return { hasError: true };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Markdown rendering crashed:", error, errorInfo);
  }

  public render() {
    if (this.state.hasError) return this.props.fallback;
    return this.props.children;
  }
}

const NxMarkdown = ({ text = "" }: { text: string }) => {
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // 1. Sanitize text: Replace escaped newlines (\\n or \\\\n) with real newlines
  const sanitizedText = useMemo(
    () => (text ? text.replace(/\\\\n/g, "\n") : ""),
    [text]
  );

  // 2. Prevent SSR mismatch / Hydration errors
  if (!isMounted) {
    return <span>{text}</span>;
  }

  return (
    <div className="markdown-wrapper px-2">
      <MarkdownErrorBoundary fallback={<span>{text}</span>}>
        <Markdown
          remarkPlugins={[remarkGfm]}
          components={{
            a: ({ ...props }) => (
              <a {...props} target="_blank" rel="noopener noreferrer" />
            ),
          }}
        >
          {sanitizedText}
        </Markdown>
      </MarkdownErrorBoundary>
    </div>
  );
};

export default NxMarkdown;
